/**
 * Overview Tab Component
 * 
 * Displays promo information in organized cards:
 * - Promo Summary
 * - Discount Configuration
 * - Usage Limits
 * - Targeting
 * - Stackability & Funding
 */

import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { Promo, ScopeFilter } from '../../../../../api/promos/types';
import '../../../Campaigns/CreateCampaign/components/SummaryCardStyles.css';
import '../../../Campaigns/CreateCampaign/components/CampaignPreview.css';
import '../PromoDetail.css';

interface OverviewTabProps {
  promo: Promo;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ promo }) => {
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  // Format benefit
  const formatBenefit = () => {
    if (!promo.benefit) return '-';
    const { type, value, maxDiscountAmount } = promo.benefit;
    if (type === 'PERCENTAGE_DISCOUNT') {
      return `${value}%${maxDiscountAmount ? ` (Max ₹${maxDiscountAmount})` : ''}`;
    } else if (type === 'FIXED_DISCOUNT') {
      return `₹${value}`;
    } else if (type === 'FREE_SHIPPING') {
      return 'Free Shipping';
    }
    return '-';
  };
  
  // Format discount level
  const formatDiscountLevel = () => {
    if (!promo.benefit) return '-';
    return promo.benefit.level === 'ORDER' ? 'Order' : 'Item';
  };
  
  // Format domain
  const formatDomain = () => {
    return promo.domain || '-';
  };
  
  // Format transaction type
  const formatTransactionType = () => {
    if (!promo.transactionType) return '-';
    const typeMap: { [key: string]: string } = {
      'ACADEMY_ENROLLMENT': 'Academy Enrollment',
      'SHOP_PURCHASE': 'Shop Purchase',
      'VENUE_BOOKING': 'Venue Booking',
      'COACHING_ENROLLMENT': 'Coaching Enrollment',
    };
    return typeMap[promo.transactionType] || promo.transactionType;
  };

  const formatVisibility = () => {
    const visibilityType = promo.visibility?.type;
    if (!visibilityType) return '-';
    if (visibilityType === 'ASSIGNED') return 'Assigned';
    if (visibilityType === 'PUBLIC') return 'Public';
    return visibilityType;
  };
  
  // Format eligibility
  const formatEligibility = () => {
    if (!promo.eligibility) return 'All Users';
    return promo.eligibility.userType === 'ALL' ? 'All Users' : promo.eligibility.userType;
  };
  
  // Format funding
  const formatFunding = () => {
    if (!promo.funding) return 'Platform';
    if (promo.funding.fundedBy === 'PLATFORM') {
      return 'Platform';
    } else if (promo.funding.fundedBy === 'SHARED') {
      return `Shared (${promo.funding.sharePercentage || 100}%)`;
    }
    return 'Platform';
  };
  
  // Format scope targeting
  const formatScopeTargeting = () => {
    if (!promo.scope || !promo.scope.entityTargeting) {
      return 'All Entities';
    }
    
    const mode = promo.scope.entityTargeting.mode;
    if (mode === 'INCLUDE') {
      return 'Specific Entities';
    } else if (mode === 'EXCLUDE') {
      return 'Exclude Specific Entities';
    }
    return 'All Entities';
  };
  
  // Get scope filters
  const getScopeFilters = () => {
    if (!promo.scope || !promo.scope.filters || promo.scope.filters.length === 0) {
      return null;
    }
    return promo.scope.filters.map((filter: ScopeFilter) => ({
      field: filter.field,
      value: Array.isArray(filter.value) ? filter.value.join(', ') : filter.value,
    }));
  };
  
  const scopeFilters = getScopeFilters();
  
  return (
    <Row className="g-4 promo-detail-page">
      {/* Promo Summary Card */}
      <Col md={4}>
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Promo Summary</h6>
          </div>
          <div className="summary-card-body">
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Promo Name</span>
              <div className="campaign-preview-content">{promo.name || '-'}</div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Promo Code</span>
              <div className="campaign-preview-content">
                {promo.code ? (
                  <code style={{ 
                    backgroundColor: '#F3F4F6', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#1F2937',
                    fontWeight: 500,
                  }}>
                    {promo.code}
                  </code>
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Domain</span>
              <div className="campaign-preview-content">{formatDomain()}</div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Transaction Type</span>
              <div className="campaign-preview-content">{formatTransactionType()}</div>
            </div>
            {promo.campaignName && (
              <div className="promo-overview-item">
                <span className="campaign-preview-label">Campaign</span>
                <div className="campaign-preview-content">{promo.campaignName}</div>
              </div>
            )}

            <div className="promo-overview-item">
              <span className="campaign-preview-label">Visibility</span>
              <div className="campaign-preview-content">{formatVisibility()}</div>
            </div>
          </div>
        </div>
      </Col>
      
      {/* Discount Configuration Card */}
      <Col md={4}>
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Discount Configuration</h6>
          </div>
          <div className="summary-card-body">
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Discount Level</span>
              <div className="campaign-preview-content">{formatDiscountLevel()}</div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Discount Type</span>
              <div className="campaign-preview-content">
                {promo.benefit?.type === 'PERCENTAGE_DISCOUNT' ? 'Percentage' : 
                 promo.benefit?.type === 'FIXED_DISCOUNT' ? 'Fixed Amount' :
                 promo.benefit?.type === 'FREE_SHIPPING' ? 'Free Shipping' : '-'}
              </div>
            </div>
            {promo.benefit?.type === 'PERCENTAGE_DISCOUNT' && (
              <div className="promo-overview-item">
                <span className="campaign-preview-label">Percentage Value (%)</span>
                <div className="campaign-preview-content">{promo.benefit.value}%</div>
              </div>
            )}
            {promo.benefit?.maxDiscountAmount && (
              <div className="promo-overview-item">
                <span className="campaign-preview-label">Max Discount Cap (₹)</span>
                <div className="campaign-preview-content">₹{promo.benefit.maxDiscountAmount}</div>
              </div>
            )}
            {promo.benefit?.type === 'FIXED_DISCOUNT' && (
              <div className="promo-overview-item">
                <span className="campaign-preview-label">Fixed Amount (₹)</span>
                <div className="campaign-preview-content">₹{promo.benefit.value}</div>
              </div>
            )}
          </div>
        </div>
      </Col>
      
      {/* Usage Limits Card */}
      <Col md={4}>
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Usage Limits</h6>
          </div>
          <div className="summary-card-body">
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Total Usage Limit</span>
              <div className="campaign-preview-content">
                {promo.usageLimits?.totalLimit ? `₹${promo.usageLimits.totalLimit}` : 'Unlimited'}
              </div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Per User Limit</span>
              <div className="campaign-preview-content">
                {promo.usageLimits?.perUserLimit || '-'}
              </div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Valid From Date</span>
              <div className="campaign-preview-content">{formatDate(promo.validity?.startAt)}</div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Expiry Date</span>
              <div className="campaign-preview-content">{formatDate(promo.validity?.endAt)}</div>
            </div>
          </div>
        </div>
      </Col>
      
      {/* Targeting Card */}
      <Col md={6}>
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Targeting</h6>
          </div>
          <div className="summary-card-body">
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Promo applies to</span>
              <div className="campaign-preview-content">{formatScopeTargeting()}</div>
            </div>
            {promo.scope?.entityTargeting?.entityIds && promo.scope.entityTargeting.entityIds.length > 0 && (
              <div className="promo-overview-item">
                <span className="campaign-preview-label">Entity IDs</span>
                <div className="campaign-preview-content">
                  {promo.scope.entityTargeting.entityIds.join(', ')}
                </div>
              </div>
            )}
            {scopeFilters && scopeFilters.map((filter: { field: string; value: string | number }, index: number) => (
              <div key={index} className="promo-overview-item">
                <span className="campaign-preview-label">Field</span>
                <div className="campaign-preview-content">{filter.field}</div>
              </div>
            ))}
            {scopeFilters && scopeFilters.map((filter: { field: string; value: string | number }, index: number) => (
              <div key={index} className="promo-overview-item">
                <span className="campaign-preview-label">Value</span>
                <div className="campaign-preview-content">{filter.value}</div>
              </div>
            ))}
          </div>
        </div>
      </Col>
      
      {/* Stackability & Funding Card */}
      <Col md={6}>
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Stackability & Funding</h6>
          </div>
          <div className="summary-card-body">
            <div className="promo-overview-item">
              <span className="campaign-preview-label">With Referral</span>
              <div className="campaign-preview-content">
                {promo.stackingRules?.allowWithReferral ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">With Others</span>
              <div className="campaign-preview-content">
                {promo.stackingRules?.allowWithOtherPromos ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Exclusive</span>
              <div className="campaign-preview-content">
                {promo.stackingRules?.exclusive ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Users Eligibility</span>
              <div className="campaign-preview-content">{formatEligibility()}</div>
            </div>
            <div className="promo-overview-item">
              <span className="campaign-preview-label">Funded By</span>
              <div className="campaign-preview-content">{formatFunding()}</div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default OverviewTab;
